import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Badge,
  Navbar,
  Nav,
} from "react-bootstrap";

// Fix leaflet's default icon path issues with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "/marker-icon-2x.png",
  iconUrl: "/marker-icon.png",
  shadowUrl: "/marker-shadow.png",
});

// API Configuration
const API_CONFIG = {
  baseUrl: "https://mycemapp.com/cem_dev/mapi/fetchcountrydata_newapp.jsp",
  accessToken: "your_hardcoded_access_token_here", // Replace with actual token
  defaultParams: {
    mcc: "545", // Kiribati
    technology: "ALL", // Single call to get up to 10,000 records
  },
  maxRecords: 10000, // Maximum records to display
};

// Color coding for signal strength
const SIGNAL_COLORS = {
  green: "#27ae60", // Good signal
  yellow: "#f1c40f", // Moderate signal
  red: "#e74c3c", // Poor signal
  gray: "#95a5a6", // Unknown/Error (not used in clustering)
};

// Threshold ranges for different technologies
const THRESHOLD_RANGES = {
  "2G": { strength: [-95, -85], quality: [6, 4] },
  "3G": { strength: [-105, -85], quality: [6, 4], power: [-124, 60] },
  "4G": { strength: [-110, -90], quality: [-14, -11], sinr: [5, 15] },
  "5G": { strength: [-110, -90], quality: [-14, -11], sinr: [5, 15] },
  "5GLte": { strength: [-110, -90], quality: [-14, -11], sinr: [5, 15] },
};

// Color determination algorithm
function getColorForValue(value, thresholdRange) {
  try {
    if (Array.isArray(thresholdRange) && thresholdRange.length === 2) {
      const [min, max] = thresholdRange;

      if (value >= min && value <= max) {
        return "green"; // Good signal
      } else if (value > max) {
        return "yellow"; // Moderate signal
      } else {
        return "red"; // Poor signal
      }
    }
  } catch (error) {
    console.error("Error parsing threshold:", error);
  }
  return "gray"; // Default color if threshold parsing fails
}

// API service function
const fetchCellInfoData = async (params) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_CONFIG.baseUrl}?${queryString}`, {
      headers: {
        Authorization: `Bearer ${API_CONFIG.accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

// Data processing function
const processApiResponse = (apiResponse) => {
  if (apiResponse.status !== 200) {
    throw new Error(apiResponse.error || "API request failed");
  }

  return {
    data: apiResponse.data || [],
    thresholds: apiResponse.thresholds || {},
    metadata: {
      mcc: apiResponse.mcc,
      technology: apiResponse.technology,
      country: apiResponse.country,
      totalRecords: apiResponse.total_records,
    },
  };
};

// Calculate map bounds from data points
const calculateMapBounds = (dataPoints) => {
  if (!dataPoints || dataPoints.length === 0) {
    return null;
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  dataPoints.forEach((point) => {
    const lat = parseFloat(point.latitude);
    const lng = parseFloat(point.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
    }
  });

  if (minLat === Infinity || maxLat === -Infinity) {
    return null;
  }

  return {
    center: [(minLat + maxLat) / 2, (minLng + maxLng) / 2],
    bounds: [
      [minLat, minLng],
      [maxLat, maxLng],
    ],
  };
};

// SVG marker icon generator for signal strength
const createSignalStrengthIcon = (signalStrengthColor, technology) => {
  const color = SIGNAL_COLORS[signalStrengthColor] || SIGNAL_COLORS.gray;

  return L.divIcon({
    className: "signal-strength-marker",
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -36],
    html: `
      <svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="16" cy="16" rx="12" ry="12" fill="${color}" stroke="#222" stroke-width="2"/>
        <path d="M16 41C16 41 28 27.5 16 27.5C4 27.5 16 41 16 41Z" fill="${color}" stroke="#222" stroke-width="2"/>
        <ellipse cx="16" cy="16" rx="5" ry="5" fill="white"/>
        <text x="16" y="20" text-anchor="middle" fill="#222" font-size="10" font-weight="bold">${technology}</text>
      </svg>
    `,
  });
};

// Custom iconCreateFunction for signal strength cluster icon (3 colors only)
function createSignalStrengthClusterIcon(cluster) {
  const children = cluster.getAllChildMarkers();
  const colorCounts = { red: 0, yellow: 0, green: 0 };

  children.forEach((child) => {
    // Try to get color from different possible sources
    let color = "red"; // Default color

    // First try to get from the marker's direct properties
    if (child.signalColor) {
      color = child.signalColor;
    } else if (child.markerColor) {
      color = child.markerColor;
    } else if (child.options && child.options.signalColor) {
      color = child.options.signalColor;
    } else if (child.options && child.options.markerColor) {
      color = child.options.markerColor;
    }

    // Debug: Log what we found for each child (only log first few to avoid spam)
    if (colorCounts.red + colorCounts.yellow + colorCounts.green < 5) {
      console.log("Child marker color detection:", {
        signalColor: child.signalColor,
        markerColor: child.markerColor,
        hasOptions: !!child.options,
        optionsSignalColor: child.options?.signalColor,
        optionsMarkerColor: child.options?.markerColor,
        finalColor: color,
      });
    }

    // Only count the 3 main signal colors, ignore gray
    if (color !== "gray") {
      colorCounts[color] = (colorCounts[color] || 0) + 1;
    }
  });

  const total = children.length;
  const redPercent = (colorCounts.red / total) * 100;
  const yellowPercent = (colorCounts.yellow / total) * 100;
  const greenPercent = (colorCounts.green / total) * 100;

  const size = 50; // Increased size for better visibility
  const radius = size / 2;
  const innerRadius = Math.round(radius * 0.4); // Smaller inner radius for clearer slices
  const pieRadius = Math.round(radius * 0.85); // Pie chart segments - 85% of radius for better visibility
  let angle = 0;

  const getArc = (percent, color) => {
    if (percent <= 0) return "";
    const startAngle = angle;
    const endAngle = angle + (percent / 100) * 360;
    angle = endAngle;
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    const start = polarToCartesian(radius, radius, pieRadius, endAngle);
    const end = polarToCartesian(radius, radius, pieRadius, startAngle);
    return `<path d="M${radius},${radius} L${start.x},${start.y} A${pieRadius},${pieRadius} 0 ${largeArc},0 ${end.x},${end.y} Z" fill="${color}" stroke="#000" stroke-width="1" />`;
  };

  function polarToCartesian(cx, cy, r, angle) {
    const a = ((angle - 90) * Math.PI) / 180.0;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  }

  angle = 0;

  // Ensure we always have at least one color segment
  let arcs = [];
  if (redPercent > 0) arcs.push(getArc(redPercent, SIGNAL_COLORS.red));
  if (yellowPercent > 0) arcs.push(getArc(yellowPercent, SIGNAL_COLORS.yellow));
  if (greenPercent > 0) arcs.push(getArc(greenPercent, SIGNAL_COLORS.green));

  // Debug: Log what arcs were generated
  console.log("Generated arcs:", arcs);
  console.log("Arc details:", {
    redPercent,
    yellowPercent,
    greenPercent,
    redArc: redPercent > 0 ? getArc(redPercent, SIGNAL_COLORS.red) : "none",
    yellowArc:
      yellowPercent > 0 ? getArc(yellowPercent, SIGNAL_COLORS.yellow) : "none",
    greenArc:
      greenPercent > 0 ? getArc(greenPercent, SIGNAL_COLORS.green) : "none",
  });

  // If no valid colors, show a default red segment
  if (arcs.length === 0) {
    arcs.push(
      `<circle cx="${radius}" cy="${radius}" r="${radius}" fill="${SIGNAL_COLORS.red}" stroke="#000" stroke-width="2" />`
    );
  }

  // Debug: Log the raw counts first
  console.log("Raw color counts:", colorCounts);
  console.log("Total markers in cluster:", total);

  // Debug logging to see what colors are being detected
  console.log(`Cluster analysis:`, {
    red: colorCounts.red,
    yellow: colorCounts.yellow,
    green: colorCounts.green,
    redPercent,
    yellowPercent,
    greenPercent,
  });

  // Create a simple fallback if no arcs are generated
  let svgContent = "";

  // Check if we have only one color type
  const hasMultipleColors =
    (colorCounts.red > 0 && colorCounts.yellow > 0) ||
    (colorCounts.red > 0 && colorCounts.green > 0) ||
    (colorCounts.yellow > 0 && colorCounts.green > 0);

  if (arcs.length > 0 && hasMultipleColors) {
    svgContent = `
      <!-- White background circle with black border -->
      <circle cx="${radius}" cy="${radius}" r="${radius}" fill="white" stroke="#000" stroke-width="3" />
      <!-- Signal strength pie chart segments (proportional representation) -->
      ${arcs.join("")}
      <!-- Inner white circle with black border -->
      <circle cx="${radius}" cy="${radius}" r="${innerRadius}" fill="rgba(255,255,255,0.95)" stroke="#000" stroke-width="1" />
    `;
  } else {
    // Single color or no arcs: show the main color as background
    let mainColor = SIGNAL_COLORS.red; // Default

    if (
      colorCounts.green > 0 &&
      colorCounts.yellow === 0 &&
      colorCounts.red === 0
    ) {
      mainColor = SIGNAL_COLORS.green; // All green
    } else if (
      colorCounts.yellow > 0 &&
      colorCounts.green === 0 &&
      colorCounts.red === 0
    ) {
      mainColor = SIGNAL_COLORS.yellow; // All yellow
    } else if (
      colorCounts.red > 0 &&
      colorCounts.green === 0 &&
      colorCounts.yellow === 0
    ) {
      mainColor = SIGNAL_COLORS.red; // All red
    } else {
      // Mixed colors: use majority
      if (
        colorCounts.yellow > colorCounts.green &&
        colorCounts.yellow > colorCounts.red
      ) {
        mainColor = SIGNAL_COLORS.yellow;
      } else if (colorCounts.green > colorCounts.red) {
        mainColor = SIGNAL_COLORS.green;
      } else {
        mainColor = SIGNAL_COLORS.red;
      }
    }

    svgContent = `
      <!-- Single color or fallback: show main color as background -->
      <circle cx="${radius}" cy="${radius}" r="${radius}" fill="${mainColor}" stroke="#000" stroke-width="3" />
      <!-- Inner white circle with black border -->
      <circle cx="${radius}" cy="${radius}" r="${innerRadius}" fill="rgba(255,255,255,0.95)" stroke="#000" stroke-width="1" />
    `;
  }

  const html = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="pointer-events:none">
      ${svgContent}
      <!-- Total count text -->
      <text x="50%" y="54%" text-anchor="middle" fill="#000" font-size="12" font-weight="bold" dy=".3em">${total}</text>
    </svg>
  `;

  return L.divIcon({
    html,
    className: "custom-cluster-icon",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2], // Center the icon properly
  });
}

function App() {
  const [apiParams, setApiParams] = useState(API_CONFIG.defaultParams);
  const [cellData, setCellData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [mapRef, setMapRef] = useState(null);

  // Read URL parameters and update API params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlMcc = urlParams.get("mcc");
    const urlRadius = urlParams.get("radius");
    const urlLat = urlParams.get("latitude");
    const urlLng = urlParams.get("longitude");
    const urlTech = urlParams.get("technology");
    const toDate = urlParams.get("to");
    const fromDate = urlParams.get("from");

    if (urlMcc || urlRadius) {
      setApiParams((prev) => ({
        ...prev,
        ...(urlMcc && { mcc: urlMcc }),
        ...(urlRadius && { radius: urlRadius }),
        ...(urlLat && { latitude: urlLat }),
        ...(urlLng && { longitude: urlLng }),
        ...(urlTech && { technology: urlTech.toUpperCase() }),
        ...(toDate && { to: toDate }),
        ...(fromDate && { from: fromDate }),
      }));
    }
  }, []);

  // Clear all map data
  const clearMapData = () => {
    console.log("Clearing previous map data");
    setCellData([]);
    setMetadata(null);
    setMapBounds(null);
    setError(null);
  };

  // Fetch data from API - single call
  const fetchData = async () => {
    console.log("Fetching new data, clearing previous data first");
    setLoading(true);
    setError(null);

    // Clear previous data before fetching new data
    clearMapData();
    console.log("API Params before fetch:", apiParams);
    if (!apiParams.mcc) {
      setError("MCC must be a 3-digit code. Please enter a valid MCC.");
      setLoading(false);
      return;
    }

    if (!apiParams.longitude || !apiParams.latitude) {
      setError("Both longitude and latitude are required.");
      setLoading(false);
      return;
    }

    if (!apiParams.radius) {
      setError("Radius must be specified.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetchCellInfoData(apiParams);
      const processedData = processApiResponse(response);

      // Process and color-code the data
      let limitedData = processedData.data.slice(0, API_CONFIG.maxRecords);

      const processedMarkers = limitedData.map((dataPoint, idx) => {
        const technology = dataPoint.technology || "Unknown";
        const thresholds =
          THRESHOLD_RANGES[technology] || THRESHOLD_RANGES["4G"];

        // Determine signal strength color
        const signalStrengthColor = getColorForValue(
          dataPoint.signal_strength,
          thresholds.strength
        );

        // For clustering, use only the 3 main colors (ignore gray)
        const clusteringColor =
          signalStrengthColor === "gray" ? "red" : signalStrengthColor;

        // Debug: Log a few examples to see what colors are being generated
        if (idx < 5) {
          console.log(`Marker ${idx} color info:`, {
            signal_strength: dataPoint.signal_strength,
            technology: technology,
            thresholds: thresholds.strength,
            signalStrengthColor: signalStrengthColor,
            clusteringColor: clusteringColor,
          });
        }

        return {
          ...dataPoint,
          geocode: [
            parseFloat(dataPoint.latitude),
            parseFloat(dataPoint.longitude),
          ],
          signal_strength_color: signalStrengthColor,
          clustering_color: clusteringColor, // Separate color for clustering
          popUp: `
            <strong>${technology}</strong><br/>
            Signal Strength: ${
              dataPoint.signal_strength
            } dBm (${signalStrengthColor})<br/>
            Signal Quality: ${dataPoint.signal_quality || "N/A"}<br/>
            MCC: ${dataPoint.mcc}<br/>
            MNC: ${dataPoint.mnc || "N/A"}<br/>
            ${dataPoint.lac ? `LAC: ${dataPoint.lac}<br/>` : ""}
            ${dataPoint.tac ? `TAC: ${dataPoint.tac}<br/>` : ""}
            ${dataPoint.sinr ? `SINR: ${dataPoint.sinr} dB<br/>` : ""}
            ${
              dataPoint.signal_power
                ? `Power: ${dataPoint.signal_power} dBm<br/>`
                : ""
            }
            ${dataPoint.ecno ? `Ec/No: ${dataPoint.ecno}<br/>` : ""}
            Timestamp: ${new Date(
              parseInt(dataPoint.timestamp)
            ).toLocaleString()}
          `,
        };
      });

      setCellData(processedMarkers);
      setMetadata(processedData.metadata);

      // Calculate and set map bounds
      const bounds = calculateMapBounds(limitedData);
      setMapBounds(bounds);

      // Auto-center map on data if available
      if (bounds && mapRef) {
        mapRef.fitBounds(bounds.bounds, { padding: [20, 20] });
      }

      // If user location is available, center map there instead
      if (apiParams && mapRef) {
        mapRef.setView([apiParams.latitude, apiParams.longitude], 10);
      }

      console.log(`Successfully loaded ${processedMarkers.length} markers`);
    } catch (err) {
      setError(err.message);
      console.error("Failed to fetch cell info data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount - removed automatic loading
  useEffect(() => {
    fetchData();
  }, [apiParams]);

  // Center map on user location when it changes
  useEffect(() => {
    if (mapRef && apiParams && apiParams.latitude && apiParams.longitude) {
      console.log("Centering map on user location:", [
        apiParams.latitude,
        apiParams.longitude,
      ]);
      mapRef.setView([apiParams.latitude, apiParams.longitude], 12);
    } else {
      console.log("Cannot center map - missing requirements:", {
        hasMapRef: !!mapRef,
        hasUserLocation: !!apiParams,
        hasLat: !!apiParams?.latitude,
        hasLng: !!apiParams?.longitude,
      });
    }
  }, [mapRef, apiParams]);

  // Handle map load
  const handleMapLoad = (map) => {
    console.log("handleMapLoad called with map:", map);
    console.log("Map initial center:", map.getCenter());
    console.log("Map initial zoom:", map.getZoom());

    // Set map reference immediately
    setMapRef(map);
    console.log("MapRef set to:", map);

    // Force center on Kiribati if no user location
    if (!apiParams.latitude || !apiParams.longitude) {
      console.log("No user location, forcing center on Kiribati");
      map.setView([1.3382, 173.0176], 6);
    }
  };

  return (
    <div className="App">
      {/* Navigation Bar */}
      <Navbar className="navbar-gradient mb-4" variant="dark" expand="lg">
        <Container fluid>
          <Navbar.Brand href="/">
            <i className="fas fa-signal text-success me-2"></i>
            CellInfo Signal Strength Map
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
        </Container>
      </Navbar>

      <Container fluid>
        {/* Compact Status Info */}
        {metadata && (
          <Row className="mb-2">
            <Col>
              <div className="d-flex justify-content-between align-items-center p-3 rounded status-bar">
                <div className="d-flex align-items-center me-4">
                  <Badge bg="primary" className="me-2 compact-badge">
                    Country
                  </Badge>
                  <span className="text-light small">{metadata.country}</span>
                </div>
                <div className="d-flex align-items-center me-4">
                  <Badge bg="secondary" className="me-2 compact-badge">
                    Technology
                  </Badge>
                  <span className="text-light small">
                    {metadata.technology}
                  </span>
                </div>
                <div className="d-flex align-items-center me-4">
                  <Badge bg="success" className="me-2 compact-badge">
                    Total
                  </Badge>
                  <span className="text-light small">
                    {metadata.totalRecords}
                  </span>
                </div>
                <div className="d-flex align-items-center me-4">
                  <Badge bg="warning" className="me-2 compact-badge">
                    MCC
                  </Badge>
                  <span className="text-light small">{metadata.mcc}</span>
                </div>
                <div className="d-flex align-items-center me-4">
                  <Badge bg="info" className="me-2 compact-badge">
                    Showing
                  </Badge>
                  <span className="text-light small">{cellData.length}</span>
                </div>
              </div>
            </Col>
          </Row>
        )}

        {/* Data Status Indicator */}
        <Row className="mb-2">
          <Col>
            <div
              className="d-flex justify-content-between align-items-center p-3 rounded"
              style={{ backgroundColor: "rgba(51, 65, 85, 0.4)" }}
            >
              <div className="d-flex align-items-center">
                <i className="fas fa-database me-2 text-light"></i>
                <span className="text-light small fw-bold">Data Statuss:</span>
              </div>
              <div className="d-flex align-items-center">
                {loading ? (
                  <Badge bg="warning" className="me-2">
                    <i className="fas fa-spinner fa-spin me-1"></i>
                    Loading...
                  </Badge>
                ) : cellData.length > 0 ? (
                  <Badge bg="success" className="me-2">
                    <i className="fas fa-check-circle me-1"></i>
                    {cellData.length} markers loaded
                  </Badge>
                ) : (
                  <Badge bg="secondary" className="me-2">
                    <i className="fas fa-info-circle me-1"></i>
                    No data loaded
                  </Badge>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Compact Error Display */}
        {error && (
          <Row className="mb-2">
            <Col>
              <div
                className="p-2 rounded"
                style={{
                  backgroundColor: "rgba(239, 68, 68, 0.2)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                }}
              >
                <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
                <span className="text-danger small">
                  <strong>Error:</strong> {error}
                </span>
              </div>
            </Col>
          </Row>
        )}

        {/* Map Container */}
        <Row>
          <Col>
            <Card
              className="shadow-lg map-card-gradient"
              style={{ border: "none" }}
            >
              <Card.Header className="bg-success text-white">
                <h6 className="mb-0">
                  <i className="fas fa-map me-2"></i>
                  Interactive Map
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                <MapContainer
                  center={
                    apiParams && apiParams.latitude && apiParams.longitude
                      ? [apiParams.latitude, apiParams.longitude]
                      : mapBounds?.center || [1.3382, 173.0176]
                  } // User location or data bounds or Kiribati default
                  zoom={apiParams ? 12 : 6}
                  style={{ height: "80vh", width: "100%" }}
                  ref={(map) => {
                    console.log("MapContainer ref:", map);
                    if (map) {
                      handleMapLoad(map);
                    }
                  }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {/* User Location Circle */}
                  {apiParams &&
                    apiParams.latitude &&
                    apiParams.longitude &&
                    apiParams.radius && (
                      <Circle
                        center={[apiParams.latitude, apiParams.longitude]}
                        radius={parseInt(apiParams.radius)}
                        pathOptions={{
                          color: "#3b82f6",
                          fillColor: "#3b82f6",
                          fillOpacity: 0.15,
                          weight: 3,
                          dashArray: "5, 5",
                        }}
                      >
                        <Popup>
                          <div className="text-center">
                            <strong>Your Location</strong>
                            <br />
                            Lat:{" "}
                            {apiParams?.latitude
                              ? parseFloat(apiParams.latitude).toFixed(6)
                              : "N/A"}
                            <br />
                            Lng:{" "}
                            {apiParams?.longitude
                              ? parseFloat(apiParams.longitude).toFixed(6)
                              : "N/A"}
                            <br />
                            Radius: {apiParams.radius}m
                          </div>
                        </Popup>
                      </Circle>
                    )}

                  {/* User Location Marker */}
                  {apiParams && apiParams.latitude && apiParams.longitude && (
                    <Marker
                      position={[apiParams.latitude, apiParams.longitude]}
                      icon={L.divIcon({
                        html: '<div style="background-color: #3b82f6; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 0 2px #3b82f6;"></div>',
                        className: "user-location-marker",
                        iconSize: [16, 16],
                        iconAnchor: [8, 8],
                      })}
                    >
                      <Popup>
                        <div className="text-center">
                          <strong>Your Location</strong>
                          <br />
                          Lat:{" "}
                          {apiParams?.latitude
                            ? parseFloat(apiParams.latitude).toFixed(6)
                            : "N/A"}
                          <br />
                          Lng:{" "}
                          {apiParams?.longitude
                            ? parseFloat(apiParams.longitude).toFixed(6)
                            : "N/A"}
                        </div>
                      </Popup>
                    </Marker>
                  )}

                  {cellData.length > 0 && (
                    <MarkerClusterGroup
                      iconCreateFunction={createSignalStrengthClusterIcon}
                      chunkedLoading
                      maxClusterRadius={80}
                      spiderfyOnMaxZoom={true}
                      showCoverageOnHover={false}
                      zoomToBoundsOnClick={true}
                      removeOutsideVisibleBounds={true}
                      animate={true}
                      animateAddingMarkers={true}
                    >
                      {cellData.map((marker, idx) => (
                        <Marker
                          key={idx}
                          position={marker.geocode}
                          icon={createSignalStrengthIcon(
                            marker.signal_strength_color,
                            marker.technology
                          )}
                          // Pass color data directly to the marker for clustering
                          signalColor={marker.clustering_color}
                          markerColor={marker.signal_strength_color}
                        >
                          <Popup>
                            <div
                              dangerouslySetInnerHTML={{ __html: marker.popUp }}
                            />
                          </Popup>
                        </Marker>
                      ))}
                    </MarkerClusterGroup>
                  )}
                </MapContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default App;
